import {
    useEffect,
    useRef,
    useState,
    useCallback,
    createPortal,
} from "@wordpress/element";
import {
    Save,
    Check,
    AlertCircle,
    Loader2,
    ArrowLeft,
    MoreHorizontal,
    Copy,
    ToggleLeft,
    Trash2,
    Search,
    Keyboard,
    Menu,
} from "lucide-react";

import { SettingsProvider, useSettings } from "./context/SettingsContext";
import {
    getSettings,
    createSettings,
    updateSettings,
    patchSettings,
    duplicateSettings,
    trashSettings,
} from "./api/settings";
import {
    useKeyboardShortcuts,
    matchesShortcut,
} from "./hooks/useKeyboardShortcuts";
import { sectionId } from "./constants/searchIndex";

import ConfirmModal from "@/admin/pages/settingsCrud/components/tableElements/ConfirmModal";
import SettingsSidebar from "./components/SettingsSidebar";
import GeneralPanel from "./components/panels/GeneralPanel";
import ZoomPanel from "./components/panels/ZoomPanel";
import ZoomWindowPanel from "./components/panels/ZoomWindowPanel";
import GalleryPanel from "./components/panels/GalleryPanel";
import LightboxPanel from "./components/panels/LightboxPanel";
import MobilePanel from "./components/panels/MobilePanel";
import VideoPanel from "./components/panels/VideoPanel";
import AssignmentsPanel from "./components/panels/AssignmentsPanel";
import QuickStartPanel from "./components/panels/QuickStartPanel";
import SearchPalette from "./components/SearchPalette";
import ShortcutsModal from "./components/ShortcutsModal";

// ── Tab metadata ──────────────────────────────────────────────────────────────
const TAB_META = {
    general: {
        title: "General Settings",
        description: "Variation image handling, preloading and spinner.",
    },
    zoom: {
        title: "Zoom Settings",
        description: "Zoom type, magnification level and lens appearance.",
    },
    "zoom-window": {
        title: "Zoom Window",
        description: "Floating window size, position and border style.",
    },
    "quick-start": {
        title: "Quick Start",
        description: "Apply a gallery layout preset to get started in seconds.",
    },
    gallery: {
        title: "Gallery & Thumbnails",
        description: "Thumbnail layout, main image display and transitions.",
    },
    lightbox: {
        title: "Lightbox",
        description: "Full-screen image viewer overlay and controls.",
    },
    mobile: {
        title: "Mobile & Touch",
        description: "Touch zoom and swipe gesture behaviour on mobile.",
    },
    video: {
        title: "Video",
        description: "YouTube, Vimeo and hosted video playback settings.",
    },
    assignments: {
        title: "Assignments",
        description: "Apply this rule to specific products or categories.",
    },
};

// ── Panel map — assignments is rendered separately (needs extra props) ─────────
const PANELS = {
    general: GeneralPanel,
    zoom: ZoomPanel,
    "zoom-window": ZoomWindowPanel,
    "quick-start": QuickStartPanel,
    gallery: GalleryPanel,
    lightbox: LightboxPanel,
    mobile: MobilePanel,
    video: VideoPanel,
};

// ── Status dot with tooltip ───────────────────────────────────────────────────
function StatusDot({ status }) {
    const isPublished = status === "publish";
    return (
        <div className="relative group flex items-center">
            <span
                className={`block h-2.5 w-2.5 rounded-full ring-2 ${
                    isPublished
                        ? "bg-emerald-400 ring-emerald-100"
                        : "bg-amber-400 ring-amber-100"
                }`}
            />
            <div className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
                {isPublished ? "Published" : "Draft"}
                <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
            </div>
        </div>
    );
}

// ── Per-rule action menu (Duplicate · Toggle status · Move to Trash) ──────────
function DetailsActionMenu({
    id,
    status,
    title,
    onToggleStatus,
    onDuplicate,
    onTrash,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({
        top: 0,
        right: 0,
        left: undefined,
    });
    const [confirmTrash, setConfirmTrash] = useState(false);
    const [confirmDupe, setConfirmDupe] = useState(false);
    const buttonRef = useRef(null);

    const calcPosition = () => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const isRTL = document.documentElement.getAttribute("dir") === "rtl";
        setPosition({
            top: rect.bottom + 4,
            right: isRTL ? undefined : window.innerWidth - rect.right,
            left: isRTL ? rect.left : undefined,
        });
    };

    useEffect(() => {
        if (isOpen) requestAnimationFrame(calcPosition);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        window.addEventListener("resize", calcPosition);
        window.addEventListener("scroll", calcPosition, true);
        return () => {
            window.removeEventListener("resize", calcPosition);
            window.removeEventListener("scroll", calcPosition, true);
        };
    }, [isOpen]);

    const close = () => setIsOpen(false);
    const isNew = id <= 0;
    const isPublished = status === "publish";
    const portalTarget = document.getElementById("wpbean-pgs-setting-rule-app");

    const dropdown = isOpen && (
        <>
            <div className="fixed inset-0 z-10" onClick={close} />
            <div
                className="fixed z-20 w-52 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                style={{
                    top: `${position.top}px`,
                    ...(position.left !== undefined
                        ? { left: `${position.left}px` }
                        : { right: `${position.right}px` }),
                }}
            >
                <button
                    onClick={() => {
                        close();
                        setConfirmDupe(true);
                    }}
                    disabled={isNew}
                    className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                    <Copy className="me-3 h-4 w-4 flex-shrink-0" />
                    Duplicate
                </button>

                <button
                    onClick={() => {
                        close();
                        onToggleStatus();
                    }}
                    disabled={isNew}
                    className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                    <ToggleLeft className="me-3 h-4 w-4 flex-shrink-0" />
                    {isPublished ? "Set to Draft" : "Publish"}
                </button>

                <hr className="my-1 border-gray-100" />

                <button
                    onClick={() => {
                        close();
                        setConfirmTrash(true);
                    }}
                    disabled={isNew}
                    className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                    <Trash2 className="me-3 h-4 w-4 flex-shrink-0" />
                    Move to Trash
                </button>
            </div>
        </>
    );

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                onClick={() => setIsOpen((o) => !o)}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                aria-label="More actions"
            >
                <MoreHorizontal className="h-4 w-4" />
            </button>

            {isOpen && portalTarget && createPortal(dropdown, portalTarget)}

            <ConfirmModal
                open={confirmTrash}
                title="Move to Trash?"
                description={`"${
                    title || "This rule"
                }" will be moved to the trash.`}
                confirmText="Move to Trash"
                danger
                onConfirm={() => {
                    setConfirmTrash(false);
                    onTrash();
                }}
                onCancel={() => setConfirmTrash(false)}
            />

            <ConfirmModal
                open={confirmDupe}
                title="Duplicate rule?"
                description={`A copy of "${
                    title || "this rule"
                }" will be created as a draft.`}
                confirmText="Duplicate"
                onConfirm={() => {
                    setConfirmDupe(false);
                    onDuplicate();
                }}
                onCancel={() => setConfirmDupe(false)}
            />
        </div>
    );
}

// ── Inner app (needs context) ─────────────────────────────────────────────────
function AppInner({ id }) {
    const { state, actions } = useSettings();
    const {
        activeTab,
        settings,
        title,
        saving,
        saved,
        error,
        isDirty,
        loaded,
    } = state;

    const {
        shortcuts,
        update: updateShortcut,
        reset: resetShortcuts,
    } = useKeyboardShortcuts();
    const [searchOpen, setSearchOpen] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const meta = TAB_META[activeTab] || TAB_META.general;
    const Panel = PANELS[activeTab] || null;
    const isAssignmentsTab = activeTab === "assignments";

    // Mirrors TopBar.jsx's liveid ref: tracks the real DB id across saves.
    // Starts as 0 when no ?id= param; updated after the first POST creates the row.
    const liveid = useRef(id);

    // Ref exposed to AssignmentsPanel — called with the final id at save time.
    const assignmentsSaveRef = useRef(null);

    // Tracks whether assignments have unsaved changes (separate from settings isDirty).
    const [assignmentsDirty, setAssignmentsDirty] = useState(false);
    const handleAssignmentsDirty = useCallback(
        () => setAssignmentsDirty(true),
        [],
    );
    const handleAssignmentsClean = useCallback(
        () => setAssignmentsDirty(false),
        [],
    );

    const effectiveDirty = isDirty || assignmentsDirty;

    // Tracks current rule status (publish / draft) for the header indicator and actions menu.
    const [status, setStatus] = useState("draft");

    // Back URL — settingsCrud listing page.
    const backUrl = (() => {
        const url = new URL(window.location.href);
        url.searchParams.set("page", "wpbean-product-gallery-settings");
        url.searchParams.delete("id");
        return url.toString();
    })();

    // GET /settings/:id — load title + config (skip when id === 0, new item)
    useEffect(() => {
        if (liveid.current > 0) {
            getSettings(liveid.current)
                .then((data) => {
                    actions.load(data?.config || {}, data?.title || "");
                    setStatus(data?.status || "draft");
                })
                .catch(() => actions.load({}, ""));
        } else {
            // New item — mark as loaded so the UI renders with defaults
            actions.load({}, "");
        }
    }, [id]);

    // Mirrors TopBar.jsx handleSave:
    //   id === 0 → POST (createSettings) then patch URL with the new id
    //   id  > 0 → PUT (updateSettings)
    // After the settings row is saved, call assignmentsSaveRef with the final id
    // so assignments are always written to the correct row.
    const handleSave = async () => {
        if (saving) return;
        actions.setSaving();
        const payload = { title, status: "publish", config: settings };
        try {
            if (liveid.current > 0) {
                await updateSettings(liveid.current, payload);
            } else {
                const { id: newId } = await createSettings(payload);
                liveid.current = Number(newId);
                // Patch URL so refreshes and subsequent saves use the new id
                const url = new URL(window.location.href);
                url.searchParams.set("id", newId);
                window.history.replaceState({}, "", url.toString());
            }
            // Save assignments atomically after the settings row exists
            if (assignmentsSaveRef.current) {
                await assignmentsSaveRef.current(liveid.current);
            }
            actions.setSaved();
            setStatus("publish");
            setTimeout(() => actions.clearSaved(), 3000);
        } catch (e) {
            actions.setError(
                e?.message || "Failed to save settings. Please try again.",
            );
        }
    };

    // ── Header action handlers ────────────────────────────────────────────────

    const handleToggleStatus = async () => {
        if (liveid.current <= 0) return;
        const newStatus = status === "publish" ? "draft" : "publish";
        try {
            await patchSettings(liveid.current, { status: newStatus });
            setStatus(newStatus);
        } catch (e) {
            actions.setError("Failed to update status.");
        }
    };

    const handleDuplicate = async () => {
        if (liveid.current <= 0) return;
        try {
            const { id: newId } = await duplicateSettings(liveid.current);
            const url = new URL(window.location.href);
            url.searchParams.set("id", newId);
            window.location.href = url.toString();
        } catch (e) {
            actions.setError("Failed to duplicate rule.");
        }
    };

    const handleTrash = async () => {
        if (liveid.current <= 0) return;
        try {
            await trashSettings(liveid.current);
            window.location.href = backUrl;
        } catch (e) {
            actions.setError("Failed to move rule to trash.");
        }
    };

    // Keep a ref to handleSave so the keyboard listener always calls the latest version
    const handleSaveRef = useRef(handleSave);
    useEffect(() => {
        handleSaveRef.current = handleSave;
    });

    // Global keyboard shortcuts
    useEffect(() => {
        const handler = (e) => {
            // Let bare key presses in text fields pass through; only intercept modifier combos
            const inInput = e.target.matches(
                "input, textarea, select, [contenteditable]",
            );
            if (inInput && !e.ctrlKey && !e.metaKey && !e.altKey) return;

            if (matchesShortcut(e, shortcuts.save)) {
                e.preventDefault();
                handleSaveRef.current();
            } else if (matchesShortcut(e, shortcuts.undo)) {
                e.preventDefault();
                actions.undo();
            } else if (matchesShortcut(e, shortcuts.redo)) {
                e.preventDefault();
                actions.redo();
            } else if (matchesShortcut(e, shortcuts.search)) {
                e.preventDefault();
                setSearchOpen(true);
            }
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [shortcuts, actions]);

    // Navigate to a search result: switch tab → wait for render → scroll + highlight
    const handleNavigate = useCallback(
        (item) => {
            actions.setTab(item.tab);
            setTimeout(() => {
                const el = document.getElementById(sectionId(item.section));
                if (!el) return;
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                el.classList.add("wcpg-search-hit");
                setTimeout(() => el.classList.remove("wcpg-search-hit"), 1800);
            }, 80);
        },
        [actions],
    );

    return (
        <div className="flex bg-[#f0f2f5] font-sans antialiased">
            {/* Backdrop — mobile only, sits behind the sidebar overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/50 wp:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* ── Left sidebar ── */}
            <SettingsSidebar
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />

            {/* ── Right content area ── */}
            <div className="flex min-w-0 flex-1 flex-col">
                {/* ── Top header bar ── */}
                <header className="sticky top-0 wpsm:top-[var(--wp-admin--admin-bar--height,32px)] z-10 flex h-[68px] shrink-0 items-center justify-between border-b border-gray-200/80 bg-white px-3 wp:px-5">
                    {/* Hamburger (mobile) + back button + section title + description (left) */}
                    <div className="flex min-w-0 items-center gap-2 wp:gap-3">
                        {/* Hamburger — mobile only */}
                        <button
                            type="button"
                            onClick={() => setSidebarOpen(true)}
                            className="flex wp:hidden h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                            aria-label="Open navigation"
                        >
                            <Menu className="h-4 w-4" />
                        </button>

                        <a
                            href={backUrl}
                            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 no-underline transition-colors hover:bg-gray-100 hover:text-gray-600"
                            aria-label="Back to all rules"
                        >
                            <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
                        </a>

                        <div className="flex min-w-0 flex-col justify-center gap-0.5">
                            <h1 className="text-[15px] font-semibold leading-tight text-gray-900">
                                {meta.title}
                            </h1>
                            <p className="hidden wp:block text-[11px] leading-tight text-gray-400">
                                {meta.description}
                            </p>
                        </div>
                    </div>

                    {/* Status dot · name input · save · action menu (right) */}
                    <div className="flex shrink-0 items-center gap-1.5 wp:gap-2.5">
                        {/* Error — desktop only */}
                        {error && (
                            <div className="hidden wp:flex items-center gap-1.5 rounded-md bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-600">
                                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                                {error}
                            </div>
                        )}

                        {/* Saved badge — desktop only */}
                        {saved && !saving && (
                            <div className="hidden wp:flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                                <Check
                                    className="h-3.5 w-3.5"
                                    strokeWidth={2.5}
                                />
                                Saved
                            </div>
                        )}

                        {/* Status indicator dot — desktop only */}
                        {loaded && (
                            <div className="hidden wp:flex items-center">
                                <StatusDot status={status} />
                            </div>
                        )}

                        {/* Settings name input — desktop only */}
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => actions.setTitle(e.target.value)}
                            placeholder="Settings name…"
                            className={[
                                "hidden wp:block h-8 w-44 rounded-lg border border-gray-200 bg-gray-50 px-3",
                                "text-xs font-medium text-gray-800 placeholder:text-gray-400",
                                "transition-colors duration-150",
                                "focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500/20",
                                "hover:border-gray-300",
                            ].join(" ")}
                        />

                        {/* Search + Shortcuts — desktop only */}
                        <button
                            type="button"
                            onClick={() => setSearchOpen(true)}
                            title="Search settings (Ctrl+K)"
                            className="hidden wp:flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        >
                            <Search className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => setShortcutsOpen(true)}
                            title="Keyboard shortcuts"
                            className="hidden wp:flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        >
                            <Keyboard className="h-4 w-4" />
                        </button>

                        {/* Save button */}
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving || !effectiveDirty}
                            className={[
                                "inline-flex h-8 items-center gap-1.5 rounded-lg px-3 wp:px-3.5 text-xs font-semibold",
                                "transition-all duration-200",
                                "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
                                saving || !effectiveDirty
                                    ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                    : "bg-blue-600 text-white shadow-sm hover:bg-blue-700 active:scale-[0.98] active:bg-blue-800",
                            ].join(" ")}
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    <span className="hidden wp:inline">
                                        Saving…
                                    </span>
                                </>
                            ) : (
                                <>
                                    <Save className="h-3.5 w-3.5" />
                                    <span className="hidden wp:inline">
                                        Save Changes
                                    </span>
                                </>
                            )}
                        </button>

                        {/* Three-dots action menu */}
                        <DetailsActionMenu
                            id={liveid.current}
                            status={status}
                            title={title}
                            onToggleStatus={handleToggleStatus}
                            onDuplicate={handleDuplicate}
                            onTrash={handleTrash}
                        />
                    </div>
                </header>

                {/* ── Panel content (page scrolls) ── */}
                <main className="px-3 py-4 wp:px-7 wp:py-6">
                    {!loaded ? (
                        <div className="flex h-48 items-center justify-center">
                            <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
                        </div>
                    ) : isAssignmentsTab ? (
                        // Assignments panel is rendered outside the generic Panel map
                        // because it needs id + assignmentsSaveRef as props.
                        <AssignmentsPanel
                            id={liveid.current}
                            assignmentsSaveRef={assignmentsSaveRef}
                            onAssignmentsDirty={handleAssignmentsDirty}
                            onAssignmentsClean={handleAssignmentsClean}
                        />
                    ) : (
                        <div className="mx-auto max-w-[680px] pb-10">
                            {Panel && <Panel />}
                        </div>
                    )}
                </main>
            </div>

            <SearchPalette
                open={searchOpen}
                onClose={() => setSearchOpen(false)}
                onNavigate={handleNavigate}
                searchShortcut={shortcuts.search}
            />

            <ShortcutsModal
                open={shortcutsOpen}
                onClose={() => setShortcutsOpen(false)}
                shortcuts={shortcuts}
                onUpdate={updateShortcut}
                onReset={resetShortcuts}
            />
        </div>
    );
}

// ── Root export ───────────────────────────────────────────────────────────────
export default function PluginSettingsApp({ id }) {
    return (
        <SettingsProvider>
            <AppInner id={id} />
        </SettingsProvider>
    );
}
